import { useState, useEffect, useRef } from 'react'
import { posthog } from '@/lib/posthog'
import { isNative } from '@/lib/platform'
import { applyTrackingConsent } from '@/lib/trackingConsent'

export const useConsent = () => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) return
    
    // On native iOS/Android, automatically give consent and skip the banner
    if (isNative()) {
      setHasConsent(true)
      localStorage.setItem('analytics-consent', 'true')
      posthog.opt_in_capturing()
      posthog.startSessionRecording()
      applyTrackingConsent(true)
      setIsLoading(false)
      hasInitialized.current = true
      return
    }
    
    // Check for existing consent decision
    const consent = localStorage.getItem('analytics-consent')
    if (consent !== null) {
      const consentValue = consent === 'true'
      setHasConsent(consentValue)
      
      // Initialize PostHog based on stored consent
      if (consentValue) {
        posthog.opt_in_capturing()
        posthog.startSessionRecording()
        applyTrackingConsent(true)
      } else {
        posthog.opt_out_capturing()
        posthog.stopSessionRecording()
        applyTrackingConsent(false)
      }
    }
    setIsLoading(false)
    hasInitialized.current = true
  }, [])

  const giveConsent = () => {
    setHasConsent(true)
    localStorage.setItem('analytics-consent', 'true')
    // Explicitly initialize PostHog when consent is given
    posthog.opt_in_capturing()
    posthog.startSessionRecording()
    applyTrackingConsent(true)
    
    // Verify recording status immediately
    setTimeout(() => {
      const isActive = posthog.sessionRecording?.status === 'active'
      console.log('[Consent] Session recording verification', {
        timestamp: new Date().toISOString(),
        sessionId: posthog.get_session_id(),
        recordingStatus: posthog.sessionRecording?.status,
        isActive
      })
    }, 500)
  }

  const revokeConsent = () => {
    setHasConsent(false)
    localStorage.setItem('analytics-consent', 'false')
    posthog.opt_out_capturing()
    posthog.stopSessionRecording()
    applyTrackingConsent(false)
  }

  return {
    hasConsent,
    isLoading,
    giveConsent,
    revokeConsent
  }
}
